import edgeAgentSource from '../../edge-device/aql_edge_camera_realtime.py?raw';
import edgeRequirements from '../../edge-device/requirements.txt?raw';
import { supabase } from '@/utils/supabase/client';
import { publicAnonKey, supabaseUrl } from '/utils/supabase/info';
import { createZipBlob, downloadBlob } from '@/app/utils/browser-zip';

const encoder = new TextEncoder();
const safeFilePart = (value: unknown) => String(value ?? 'kit-edge')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9._-]+/g, '-')
  .replace(/^-|-$/g, '') || 'kit-edge';

const shellValue = (value: unknown) => `'${String(value ?? '').replace(/'/g, `'"'"'`)}'`;

export async function downloadKitEdgeAgent(kitId: string) {
  const [{ data: kit, error: kitError }, { data: cameras, error: camerasError }] = await Promise.all([
    supabase.from('aql_kits').select('*').eq('kit_id', kitId).single(),
    supabase.from('aql_cameras').select('*').eq('kit_id', kitId).eq('is_active', true).order('is_primary', { ascending: false }).order('created_at', { ascending: true })
  ]);
  if (kitError || !kit) throw new Error(kitError?.message ?? 'Não foi possível obter a configuração da unidade Edge.');
  if (camerasError) throw new Error(camerasError.message);

  const camera = cameras?.[0] ?? null;
  const cameraCode = camera?.camera_code ?? `cam-${String(camera?.camera_id ?? kitId).slice(0, 8)}`;
  const frameInterval = Number(camera?.frame_interval_ms ?? kit.frame_interval_ms ?? 1000);
  const captureBucket = String(kit.live_capture_bucket ?? 'captures');
  const livePath = String(camera?.live_feed_path ?? `live/${cameraCode}/feed.jpg`);
  const kitName = String(kit.name ?? kitId);
  const bundleName = `aql-vision-agent-${safeFilePart(kitName)}`;

  const environment = [
    `SUPABASE_URL=${shellValue(supabaseUrl)}`,
    `SUPABASE_KEY=${shellValue(publicAnonKey)}`,
    "AQL_DEVICE_TOKEN=''",
    `AQL_KIT_ID=${shellValue(kitId)}`,
    `AQL_KIT_NAME=${shellValue(kitName)}`,
    `AQL_CAMERA_ID=${shellValue(camera?.camera_id ?? '')}`,
    `AQL_CAMERA_CODE=${shellValue(cameraCode)}`,
    `AQL_FRAME_INTERVAL_MS=${shellValue(Number.isFinite(frameInterval) ? frameInterval : 1000)}`,
    `AQL_CAPTURES_BUCKET=${shellValue(captureBucket)}`,
    `AQL_LIVE_FRAME_PATH=${shellValue(livePath)}`,
    "AQL_UPLOAD_MODE='function'",
    "AQL_CAMERA_BACKEND='opencv'"
  ].join('\n');

  const installScript = `#!/usr/bin/env bash
set -euo pipefail
cd "$(dirname "$0")"
python3 -m venv --system-site-packages .venv
.venv/bin/python -m pip install --upgrade pip
.venv/bin/python -m pip install -r requirements.txt
if [ ! -f .env ]; then cp .env.example .env; fi
echo "Agente instalado. Confirma a configuração em .env e executa ./run.sh"
`;

  const runScript = `#!/usr/bin/env bash
set -euo pipefail
cd "$(dirname "$0")"
if [ ! -f .env ]; then echo "Falta o ficheiro .env. Copia .env.example para .env."; exit 1; fi
set -a
. ./.env
set +a
exec .venv/bin/python aql_edge_camera_realtime.py
`;

  const readme = `# Agente AQL Vision — ${kitName}

Pacote preparado para a unidade Edge **${kitName}** e para a câmara **${camera?.name ?? cameraCode}**.

1. Extrai o ZIP no dispositivo Edge.
2. Executa \`chmod +x install.sh run.sh\`.
3. Executa \`./install.sh\`.
4. Se a instalação tiver um token de dispositivo configurado, coloca-o em \`AQL_DEVICE_TOKEN\`; caso contrário deixa-o vazio.
5. Executa \`./run.sh\`.

O agente publica o frame em \`${captureBucket}/${livePath}\` e recebe alterações de configuração sem guardar credenciais privadas no AQL Vision.

Para Raspberry Pi com câmara CSI, instala primeiro Picamera2 e muda \`AQL_CAMERA_BACKEND\` para \`picamera2\`.
`;

  const blob = createZipBlob([
    { name: `${bundleName}/aql_edge_camera_realtime.py`, data: encoder.encode(edgeAgentSource) },
    { name: `${bundleName}/requirements.txt`, data: encoder.encode(edgeRequirements) },
    { name: `${bundleName}/.env.example`, data: encoder.encode(environment) },
    { name: `${bundleName}/install.sh`, data: encoder.encode(installScript) },
    { name: `${bundleName}/run.sh`, data: encoder.encode(runScript) },
    { name: `${bundleName}/README.md`, data: encoder.encode(readme) }
  ]);
  downloadBlob(blob, `${bundleName}.zip`);
}
